import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Wrench, Send, Loader2 } from 'lucide-react';

const STARTERS = [
    'Rough idle on my 2019 Challenger 5.7 HEMI',
    'My Mustang GT 5.0 has a tick at idle',
    'Audi S4 has check engine code P0299',
    'Hellcat overheating in traffic',
    'My EcoBoost Mustang feels like underboost',
];

export default function AiMechanic() {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [vehicle, setVehicle] = useState('');
    const [sending, setSending] = useState(false);
    const scroller = useRef(null);

    useEffect(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    }, [messages]);

    const send = async (text) => {
        const t = (text || input).trim();
        if (!t) return;
        setMessages((m) => [...m, { role: 'user', content: t }]);
        setInput('');
        setSending(true);
        try {
            const { data } = await api.post('/chat', { session_id: sessionId, message: t, vehicle: vehicle || null });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { role: 'assistant', content: '⚠️ Mechanic AI is offline right now. Try again in a moment.' }]);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-md bg-amber-500/15 border border-amber-500/30 inline-flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                    <h1 className="font-display text-3xl text-white">RRM MECHANIC AI</h1>
                    <div className="text-xs text-white/50">Expert diagnostic chat for Dodge HEMI, Mustang 2.3T/5.0, Audi sedans.</div>
                </div>
            </div>

            <div className="rrm-panel p-5">
                <input
                    value={vehicle}
                    onChange={(e) => setVehicle(e.target.value)}
                    placeholder="Your vehicle (optional) — e.g. 2019 Dodge Challenger Scat Pack 6.4"
                    data-testid="ai-mechanic-vehicle-input"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
            </div>

            <div ref={scroller} className="mt-4 rrm-panel p-5 h-[55vh] min-h-[420px] overflow-y-auto space-y-3">
                {messages.length === 0 ? (
                    <div>
                        <div className="text-white/70 mb-3">Describe your symptom or pick one:</div>
                        <div className="flex flex-wrap gap-2">
                            {STARTERS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => send(s)}
                                    data-testid="ai-mechanic-page-starter"
                                    className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    messages.map((m, i) => (
                        <div
                            key={m.created_at ? `${m.role}-${m.created_at}-${i}` : `${m.role}-${i}-${m.content.length}`}
                            className={`text-sm leading-relaxed whitespace-pre-wrap rounded-lg px-4 py-3 border max-w-[88%] ${
                                m.role === 'user'
                                    ? 'bg-white/5 border-white/10 ml-auto'
                                    : 'bg-amber-500/8 border-amber-500/20'
                            }`}
                        >
                            {m.content}
                        </div>
                    ))
                )}
                {sending && (
                    <div className="text-xs text-white/50 inline-flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> mechanic thinking…
                    </div>
                )}
            </div>

            <div className="mt-3 flex items-center gap-2">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && send()}
                    placeholder="Describe the symptom in detail…"
                    data-testid="ai-mechanic-input"
                    className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
                <button
                    onClick={() => send()}
                    disabled={sending || !input.trim()}
                    data-testid="ai-mechanic-send"
                    className="px-4 py-3 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white inline-flex items-center gap-2 disabled:opacity-50"
                >
                    <Send className="w-4 h-4" /> Send
                </button>
            </div>
            <div className="mt-3 text-xs text-white/40">
                ⚠️ Guidance only. Confirm with a certified mechanic before major repairs.
            </div>
        </div>
    );
}
